import { makeStyles } from '@mui/styles';
import * as React from "react";
import { useEffect, useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    ImageInput,
    ImageField,
    DateInput,
    SelectInput,
    ReferenceInput,
    SelectArrayInput,
    ReferenceArrayInput,
    FileField,
    FileInput,
    DateTimeInput,
    useGetOne,
    useRecordContext,
    useGetList,
    TranslatableInputs,
    useLocaleState,
    useTranslate
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { DisplayStatus } from '../../shared/enums/display-status.enum';
import { Status } from "../../shared/enums/status.enum";
import { TimePeriod } from '../../shared/interfaces/timePeriod.interface';
import GetTimePeriodRange from '../../shared/services/timePeriod.services';
import { CustomToolbar } from './../../components/customtoolbar/customToolbar.component';
import { locales } from '../../i18n/languges'
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';

// Helper function to safely get nested translated field
const getTranslatedField = (record: any, field: string, locale: string): string => {
    if (!record) return '';
    
    const parts = field.split('.');
    let value = record;
    
    // Navigate through the nested path
    for (const part of parts) {
        if (value && typeof value === 'object' && part in value) {
            value = value[part];
        } else {
            return '';
        }
    }
    
    // If value is null or undefined, return empty string
    if (value == null) return '';
    
    // If we have a translation object
    if (typeof value === 'object' && !Array.isArray(value)) {
        // Check for nested structure: {"sv": {"en": "...", "de": "...", "sv": "..."}}
        // This is a database error where translations are wrapped in an extra "sv" key
        if ('sv' in value && typeof value.sv === 'object' && !Array.isArray(value.sv)) {
            // Use the inner object instead
            value = value.sv;
        }
        
        // Try the requested locale first
        if (locale in value && value[locale] != null) {
            const translated = value[locale];
            if (typeof translated === 'string') {
                return translated;
            }
        }
        
        // Fallback: try other common locales in priority order
        const fallbackLocales = ['sv', 'en', 'de'];
        for (const fallbackLocale of fallbackLocales) {
            if (fallbackLocale in value && value[fallbackLocale] != null) {
                const translated = value[fallbackLocale];
                if (typeof translated === 'string') {
                    return translated;
                }
            }
        }
        
        // Last resort: try to get any string value from the object
        for (const key in value) {
            if (typeof value[key] === 'string' && value[key]) {
                return value[key];
            }
        }
        
        // If still nothing found, return empty string
        return '';
    }
    
    // If it's already a string, return it
    if (typeof value === 'string') {
        return value;
    }
    
    // For any other type, convert to string
    return String(value);
};

const useStyles = makeStyles({
    inlineBlock: {
        display: 'inline-flex',
        marginRight: '1rem'
    },
});

export const PathEdit = () => {
    const classes = useStyles();
    const translate = useTranslate();
    const timePeriods = useGetList<TimePeriod>(
        "timeperiods",
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
        },
    );
    const record = useRecordContext();
    const [selectedTimePeriodId, setSelectedTimePeriodId] = useState<number>(0);
    const [displayValue, setDisplayValue] = useState(DisplayStatus.Display);
    const { id } = useParams();
    const [locale] = useLocaleState();
    const path = useGetOne("paths", { id: id ?? record?.id });
    const selectedTimePeriod = GetTimePeriodRange(
        selectedTimePeriodId,
        timePeriods.data as any
    );
    const displayOptions = Object.keys(DisplayStatus).map(key => ({
        id: DisplayStatus[key],
        name: `resources.enums.displayStatus.${key}`,
    }));

    const statusOptions = Object.keys(Status).map((key) => ({
        id: Status[key],
        name: key === Status.notSet ? '---' : translate(`resources.paths.fields.${key}`),
      }));

    const schema = yup.object().shape({
        name: yup.object().shape({
            en: yup.string().required(),
        }).required(),
        number: yup.string().required(),
        length: yup.number().required().positive(),
        difficulty: yup.string().required(),
        status: yup.string().required(),
        pathCategoryId: yup.number().required(),
        area_ids: yup.array().of(yup.number()).required(),
        display: yup.string().required(),
        timePeriodId: yup.string().nullable().when('display', {
            is: (value: string | undefined) => value === DisplayStatus.Scheduled,
            then: schema => schema.required('Time period can\'t be empty if display is scheduled'),
            otherwise: schema => schema.nullable()
        }),
    });

    const resolver = yupResolver<FieldValues>(schema);

    useEffect(() => {
        if (!path.isLoading) {
            setSelectedTimePeriodId(path?.data?.timePeriodId);
            setDisplayValue(path?.data?.display);
        }
    }, [path.isLoading]);

    return (
        <Edit id={record?.id}>
            <SimpleForm toolbar={<CustomToolbar />} resolver={resolver} >
                <TranslatableInputs fullWidth={true} locales={locales} sx={{ marginBottom: '2rem' }} >
                    <TextInput source="name" />
                    <TextInput source="start" />
                    <TextInput source="end" />
                    <TextInput source="description" rows={8} multiline={true} fullWidth={true} />
                </TranslatableInputs>
                <TextInput source="number" />
                <TextInput source="length" />
                <DateTimeInput source="groomedAt" />
                <TextInput source="lastGPXFilename" disabled />
                <SelectInput source="difficulty" defaultValue={'medium'} choices={[
                    { id: 'veryEasy', name: 'resources.paths.fields.veryEasy' },
                    { id: 'easy', name: 'resources.paths.fields.easy' },
                    { id: 'medium', name: 'resources.paths.fields.medium' },
                    { id: 'hard', name: 'resources.paths.fields.hard' },
                ]} />
                <SelectInput source={'status'} label="resources.paths.fields.status" defaultValue={'open' } choices={statusOptions} />
                <ReferenceInput source="pathCategoryId" reference="pathcategories">
                    <SelectInput optionText={(record) => getTranslatedField(record, 'name', locale)} label="resources.paths.fields.category" />
                </ReferenceInput>
                <ReferenceArrayInput source="area_ids" reference="areas">
                    <SelectArrayInput optionText={(record) => getTranslatedField(record, 'name', locale)} label="resources.paths.fields.areas" />
                </ReferenceArrayInput>
                <ReferenceArrayInput source="poi_ids" reference="pois">
                    <SelectArrayInput label="resources.paths.fields.relatedPois" optionText={record => {
                        if (record.areas && record.areas.length > 0) {
                            const areaNames = record.areas.map(area => getTranslatedField(area, 'name', locale)).join(', ');
                            return `${getTranslatedField(record, 'name', locale)} (${areaNames})`;
                        }
                        return getTranslatedField(record, 'name', locale);
                    }}/>
                </ReferenceArrayInput>
                <ReferenceArrayInput source="tag_ids" reference="tags">
                    <SelectArrayInput optionText={(record) => getTranslatedField(record, 'name', locale)} label="resources.paths.fields.tags" />
                </ReferenceArrayInput>
                <FileInput source="gpx">
                    <FileField source="src" title="title" />
                </FileInput>
                <SelectInput
                    source="display"
                    choices={displayOptions}
                    onChange={(e) => setDisplayValue(e.target.value as DisplayStatus)}
                ></SelectInput>
                {displayValue === DisplayStatus.Scheduled && (
                    <ReferenceInput
                        source="timePeriodId"
                        reference="timeperiods"
                        onChange={(e) => setSelectedTimePeriodId(e.target.value)}
                    >
                        <SelectInput
                            optionText="name"
                            helperText={selectedTimePeriod}
                        ></SelectInput>
                    </ReferenceInput>
                )}
                <ImageInput source="medias" multiple={true}>
                    <ImageField source="src" title="title" />
                </ImageInput>
                <TextInput source="youtubeVideoId" label="YouTube id" className={classes.inlineBlock}/>

                <TextInput source="socialMedia.instagramUrl" label="Instagram url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.facebookUrl" label="Facebook url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.linkedinUrl" label="Linkedin url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.twitterUrl" label="Twitter url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.vimeoUrl" label="Vimeo url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.youtubeUrl" label="Youtube url" className={classes.inlineBlock}/>
                <TextInput source="socialMedia.tripadvisorUrl" label="Tripadvisor url" className={classes.inlineBlock}/>
                <DateInput disabled source="createdAt" className={classes.inlineBlock} />
                <DateInput disabled source="updatedAt" className={classes.inlineBlock} />
            </SimpleForm>
        </Edit>
    );
}